import React, { useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTenant } from '@/contexts/TenantContext';
import { useThemeStore } from '@core/theme/useThemeStore';
import { setGlobalTenantId } from '@core/lib/webApolloClient';
import { useUserStore } from '@core/user/useUserStore';
import { useTemplateStore } from '@core/template/useTemplateStore';
import { resolveAppContext } from '@core/portal/clientPortal.util';
import { AppContext } from '@core/type/types';
import { ThemeType } from '@core/generated/gql-types';

interface TenantInitializerProps {
  children: React.ReactNode;
}

const TenantInitializer: React.FC<TenantInitializerProps> = ({ children }) => {
  const { tenantId, tenantInfo, logoUrl, isLoading, error } = useTenant();
  const { fetchTheme } = useThemeStore();
  const currentUser = useUserStore((state) => state.currentUser);
  const { fetchAndSetTemplate } = useTemplateStore();

  // Set global tenant ID before child effects (e.g. AuthProvider checkAuth) run.
  useLayoutEffect(() => {
    if (tenantId && !isLoading) {
      setGlobalTenantId(tenantId);
    }
  }, [tenantId, isLoading]);

  // Fill the template store.
  useEffect(() => {
    if (currentUser && tenantId) {
      // Fill the template store once the user is logged in and the tenant ID is available.
      fetchAndSetTemplate(tenantId);
    }
  }, [tenantId, fetchAndSetTemplate, currentUser]);

  // Fetch theme when tenant ID is available
  useEffect(() => {
    if (tenantId && !isLoading) {
      const portalAppContext = resolveAppContext(currentUser?.role);
      void fetchTheme(
        tenantId,
        portalAppContext === AppContext.ClientApp
          ? ThemeType.ClientWeb
          : ThemeType.TenantWeb,
      );
    }
  }, [tenantId, isLoading, fetchTheme, currentUser?.role]);

  // Show loading state while resolving tenant
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Initializing tenant...</p>
        </div>
      </div>
    );
  }

  // Show error state if tenant resolution failed
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="mx-auto max-w-md p-6 text-center">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <h2 className="mb-2 text-lg font-semibold text-red-800">
              Tenant Resolution Failed
            </h2>
            <p className="mb-4 text-red-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="rounded bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Determine title and favicon based on tenant
  const pageTitle = tenantInfo?.tenantName
    ? `${tenantInfo.tenantName}`
    : 'Zrec';
  const faviconUrl =
    logoUrl ||
    'https://zrec.com.au/hubfs/raw_assets/public/ZREC%202025/images/favicon-1.png';

  return (
    <>
      <Helmet
        title={pageTitle}
        link={[
          {
            rel: 'icon',
            type: 'image/png',
            href: faviconUrl,
          },
        ]}
        meta={[
          {
            name: 'description',
            content: `${tenantInfo?.tenantName} - Insta Agency Platform`,
          },
          {
            property: 'og:image',
            content: faviconUrl,
          },
          {
            property: 'og:title',
            content: pageTitle,
          },
          {
            property: 'og:description',
            content: `${tenantInfo?.tenantName} - Insta Agency Platform`,
          },
        ]}
      />
      {children}
    </>
  );
};

export default TenantInitializer;
